import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import {
  Box,
  Grid,
  Typography,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Button
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import UploadRecordingModal from './components/UploadRecordingModal';

import { AppDispatch } from '../../../store';
import UserProfileTab from '../../../components/UserProfileTab';
// import dayjs from 'dayjs';
import VerificationIcon from '../../../assets/main/verification-icon.svg'
import PageCard from '../../../components/PageCard';
import CardTitle from '../../../components/CardTitle'

import {
  CustomCard,
  ButtonGroupContainer,
  VerificationImageContainer,
  DetailColumn,
  DetailLabel,
  DetailRow,
  DetailsTitle,
  DetailValue,
  InterviewDetailsCard,
  VideoPlayerBox,
  VideoPlayerText,
  FeedbackContent,
  FeedbackNotesCard,
  FeedbackTitle,
} from './styles';

// redux
import {
  fetchWorkerDetail,
  selectWorkerDetails,
  IWorkerUser,
} from '../../../modules/worker';

import {
  getInterviewDetail,
  selectInterviewDetail,
  selectInterviewDetailLoading,
  markInterviewAsViewed,
} from '../../../modules/interview';
import {
  fetchS3File,
} from '../../../modules/file/actions';

import AlertPopup from '../../../components/common/AlertPopup';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';

const InterviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { setPath, setIsShow } = useBreadcrumb();

  const [openUploadModal, setOpenUploadModal] = useState(false);
  const dispatch: AppDispatch = useDispatch();

  const workerDetails = useSelector(selectWorkerDetails);
  const interviewDetailsList = useSelector(selectInterviewDetail);
  const loadingInterview = useSelector(selectInterviewDetailLoading);

  const [workerId, setWokerId] = useState<number>(0)
  const [workerOnboardingId, setWorkerOnboardingId] = useState<number>(0)
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);
  const [workerStatusRadio, setWorkerStatusRadio] = useState<string>('block');

  const [confirmation, setConfirmation] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: (() => void) | null
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: null
  });

  const [isPlaying, setIsPlaying] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [user, setUser] = useState<IWorkerUser>({
    email: "",
    first_name: "",
    last_name: "",
    phone_number: "",
    profile_picture: ""
  })

  useEffect(() => {
    setPath([
      { title: 'Management', path: '/' },
      { title: 'Worker', path: '/workers' },
      { title: 'Interview', path: '' },
    ]);
    setIsShow(true);
  }, [setPath, setIsShow]);

  useEffect(() => {
    try {
      if (id) {
        const workerId = parseInt(id)
        setWokerId(workerId)
        dispatch(fetchWorkerDetail(workerId) as any);
      }
    } catch (err) {
      console.log(err);
    }
  }, [dispatch, id])

  const interview = interviewDetailsList && interviewDetailsList.length > 0 ? interviewDetailsList[0] : null;

  useEffect(() => {
    if (workerDetails?.worker_onboarding_id) {
      dispatch(getInterviewDetail(workerDetails.worker_onboarding_id) as any);
    }
  }, [workerDetails?.worker_onboarding_id, dispatch])

  useEffect(() => {
    if (workerDetails) {
      setUser(workerDetails.user)
      setWorkerOnboardingId(workerDetails.worker_onboarding_id)
    }
    if (interview) {
      if (interview.is_viewed) {
        setWorkerStatusRadio("accept")
      } else {
        setWorkerStatusRadio("block")
      }
    } else if (workerDetails && workerDetails.worker.status) {
      // Fallback to worker status if interview details not loaded yet? 
      // Or strictly follow request? The request says "with this is_viewed".
      // Let's prioritize is_viewed if available, else standard behavior.
      if (workerDetails.worker.status === 'active') {
        setWorkerStatusRadio("accept")
      } else {
        setWorkerStatusRadio(workerDetails.worker.status)
      }
    }
  }, [workerDetails, interview])




  const [isVideoLoading, setIsVideoLoading] = useState(false);

  const handlePlayRecording = async () => {
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }

    if (videoUrl) {
      setIsPlaying(true);
      return;
    }

    if (isVideoLoading) return;

    if (interview?.recording_link) {
      setIsVideoLoading(true);
      try {
        const resultAction = await dispatch(fetchS3File({ filePath: interview.recording_link }));
        if (fetchS3File.fulfilled.match(resultAction)) {
          const blob = resultAction.payload as Blob;
          const url = URL.createObjectURL(blob);
          setVideoUrl(url);
          setIsPlaying(true);
        } else {
          setAlert({ message: 'Failed to load recording', severity: 'error' });
        }
      } catch (error) {
        console.error('Error fetching file:', error);
        setAlert({ message: 'Error fetching recording', severity: 'error' });
      } finally {
        setIsVideoLoading(false);
      }
    } else {
      setAlert({ message: 'No recording available', severity: 'error' });
    }
  };


  return (
    <Box>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}

      <UploadRecordingModal
        open={openUploadModal}
        onClose={() => setOpenUploadModal(false)}
        interviewId={interview?.interview_id || 0}
        workerId={workerOnboardingId}
      />

      {workerDetails &&
        <PageCard padding={4} backgroundRequired={true}>
          <UserProfileTab
            user={user}
            id={workerId}
            status={workerDetails.worker.status}
          />
        </PageCard>
      }
      <PageCard padding={2} backgroundRequired={true}>

        <CustomCard>
          <CardTitle title='Interview Recording' />

          {loadingInterview ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : interview ? (
            <Box sx={{ display: 'flex', gap: 3, mb: 3, flexDirection: { xs: 'column', md: 'row' } }} mt={3}>
              <Box sx={{ flex: '1 1 58%' }}>
                {interview?.recording_link ? (
                  isPlaying && videoUrl ? (
                    <VideoPlayerBox>
                      <video controls style={{ width: '100%', height: '100%', objectFit: 'contain' }} autoPlay>
                        <source src={videoUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </VideoPlayerBox>
                  ) : (
                    <VideoPlayerBox onClick={handlePlayRecording}>
                      {isVideoLoading ? (
                        <CircularProgress color="inherit" />
                      ) : (
                        <VideoPlayerText>Click to Play Recording</VideoPlayerText>
                      )}
                    </VideoPlayerBox>
                  )
                ) : (
                  <VideoPlayerBox sx={{ flexDirection: 'column', gap: 2 }}>
                    <VideocamIcon sx={{ fontSize: 60, color: '#e0e0e0' }} />
                    <Typography sx={{ color: '#e0e0e0', mb: 1 }}>No video uploaded yet</Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setOpenUploadModal(true)}
                      sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                      Upload Video
                    </Button>
                  </VideoPlayerBox>
                )}
              </Box>

              <Box sx={{ flex: '1 1 42%' }}>
                <InterviewDetailsCard>
                  <DetailsTitle>Interview Details</DetailsTitle>

                  <DetailRow>
                    <DetailColumn>
                      <DetailLabel>Type</DetailLabel>
                      <DetailValue>{interview.title}</DetailValue>
                    </DetailColumn>
                    <DetailColumn>
                      <DetailLabel>Interviewer</DetailLabel>
                      <DetailValue>{interview.interviewer_name || 'N/A'}</DetailValue>
                    </DetailColumn>
                  </DetailRow>

                  <DetailRow>
                    <DetailColumn>
                      <DetailLabel>Date</DetailLabel>
                      <DetailValue>{interview.date}</DetailValue>
                    </DetailColumn>
                    <DetailColumn>
                      <DetailLabel>Time</DetailLabel>
                      <DetailValue>{interview.time}</DetailValue>
                    </DetailColumn>
                  </DetailRow>

                  {/* Duration is not in the API response yet, so we can hide or keep static for now if needed.
                Keeping it static or removed based on requirement. Assuming removed if data not available or keep static if placeholder needed.
                Let's keep it if we had a field, but since we don't, maybe we can omit it or show a placeholder?
                The original code had 05:32.
            */}
                  {/* <Box>
                  <DetailLabel>Duration</DetailLabel>
                  <DetailValue>05:32</DetailValue>
                </Box> */}
                </InterviewDetailsCard>
              </Box>
            </Box>
          ) : (
            <Box mt={3} mb={3}>
              <Typography variant="body1">No interview details available.</Typography>
            </Box>
          )}

          {!loadingInterview && interview && (
            <FeedbackNotesCard>
              <FeedbackTitle>Feedback Notes</FeedbackTitle>
              <FeedbackContent>
                {interview.remark || "No feedback notes available."}
              </FeedbackContent>
            </FeedbackNotesCard>
          )}


        </CustomCard>

      </PageCard>

      <PageCard padding={2} backgroundRequired={true}>
        <Grid container>
          <Grid size={6}>
            <Grid container spacing={2}>
              <VerificationImageContainer size={1}>
                <img src={VerificationIcon} alt="Verification Icon" />
              </VerificationImageContainer>
              <Grid size={11}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Verification Action
                </Typography>
                <Typography variant='subtitle2' fontWeight="body2">
                  Confirm the worker's eligibility and finalize their status
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={6} >
            <Box flexShrink={0} textAlign="right">
              <ButtonGroupContainer>
                <FormControl>
                  <RadioGroup
                    aria-labelledby="eapprove-radio-buttons-group-label"
                    value={workerStatusRadio}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const newStatus = event.target.value;
                      setConfirmation({
                        open: true,
                        title: newStatus === 'accept' ? 'Confirm Mark as Viewed' : 'Confirm Blocking',
                        message: newStatus === 'accept' ? 'Are you sure you want to mark this worker as viewed?' : 'Are you sure you want to block this worker?',
                        onConfirm: async () => {
                          setWorkerStatusRadio(newStatus);
                          if (workerId) {
                            if (newStatus === 'accept') {
                              if (interview?.interview_id) {
                                const resultAction = await dispatch(markInterviewAsViewed(interview.interview_id) as any);
                                if (markInterviewAsViewed.fulfilled.match(resultAction)) {
                                  setAlert({ message: 'Interview marked as viewed successfully', severity: 'success' });
                                  if (workerOnboardingId) {
                                    dispatch(getInterviewDetail(workerOnboardingId) as any);
                                  }
                                } else {
                                  setAlert({ message: (resultAction.payload as string) || 'Failed to mark interview as viewed', severity: 'error' });
                                }
                              } else {
                                setAlert({ message: 'No interview details available to identify context.', severity: 'error' });
                              }
                            }
                          }
                          setConfirmation({ open: false, title: '', message: '', onConfirm: null });
                        }
                      });
                    }}
                    name="radio-buttons-group"
                  >
                    <FormControlLabel value="accept" control={<Radio />} label="Approve" />
                  </RadioGroup>
                </FormControl>
              </ButtonGroupContainer>
            </Box>
          </Grid>
        </Grid>
      </PageCard>
      <ConfirmationDialog
        open={confirmation.open}
        onClose={() => setConfirmation({ open: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmation.onConfirm || (() => { })}
        title={confirmation.title}
        message={confirmation.message}
      />
    </Box>
  );
};

export default InterviewPage;